import { Search } from '@material-ui/icons';
import { format } from 'date-fns';
import PropTypes from 'prop-types';
import React from 'react';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';

import { Tooltip } from '@material-ui/core';
import { Button, EmptyStateWithAction, Text } from '../../../../../components';
import FloatBatchActionsMenu from '../../../../../components/DataTable/FloatBatchActionsMenu';
import {
  Table,
  TdContainer,
  ThContainer,
  Tr,
} from '../../../../../components/Table/Table';
import TouchableText from '../../../../../components/TouchableText';
import {
  BreakpointValues,
  Colors,
  OrderStatusEnum,
  OrderStatusText,
  USER_ROLES,
} from '../../../../../constants';
import { RoutePaths } from '../../../../../constants/RoutePaths';
import { OrderStatusERPText } from '../../../../../constants/OrderStatusERP';
import { columnLabel, columnType } from '../../../../../constants/order';
import { mediaHelper, useWindowSize } from '../../../../../utils';
import { OrderStatusERPIcon } from '../../OrderStatusERPIcon';
import { OrderCategoryEnum } from '../../../../../constants/OrderCategory';

const ContainerText = styled.div`
  display: flex;
  /* align-items: center; */
  gap: 8px;
`;

const ActionsContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
`;

const StyledTouchableText = styled(TouchableText)`
  color: #fff;
  font-size: 12px;
  margin-left: 24px;
`;

const DoubleRowWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

const DobleRowPrimaryValue = styled.span`
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
`;

const DobleRowSecondaryValue = styled.span`
  font-size: 12px;
  color: #000000;
  opacity: 0.6;
  line-height: 20px;
`;

const StyledEmptyStateWithAction = styled(EmptyStateWithAction)`
  padding: 40px 16px;
  border-bottom: 1px solid ${Colors.GRAY_LIGHTER};
  flex-direction: column;
`;

const StyledEmptyActionsContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 8px;
  flex-direction: column;

  ${mediaHelper.lg`
    flex-direction: row;
  `}

  button {
    margin-bottom: 8px;
  }
`;

const ButtonDivider = styled.div`
  width: 16px;
  height: 1px;
`;

const RowWrapper = styled(Tr)`
  &:hover {
    background-color: ${Colors.Alpha(Colors.GRAY_LIGHT, 0.3)};
  }

  ${({ checked }) => checked
    && `
    background-color: ${Colors.Alpha(Colors.PRIMARY_BLUE, 0.05)};
  `}
`;

const backgroundColorByType = {
  amostra_gratis: Colors.LAVANDER_LIGHT,
  amostra_gratis_tributada: Colors.BLUE_GRAY_LIGHT,
};

const StyledTypeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  border-radius: 16px;
  background-color: ${({ type }) => backgroundColorByType[type] ?? Colors.BLUE_VIVID_LIGHT};
  padding: 6px 10px;
`;

const colorsByType = {
  amostra_gratis: Colors.LAVANDER_DARK,
  amostra_gratis_tributada: Colors.BLUE_GRAY_DARK,
};

const StyledTypeText = styled(Text)`
  color: ${({ type }) => colorsByType[type] ?? Colors.GREEN_LIGHT};
  text-transform: uppercase;
  font-size: 14px;
  font-weight: 700;
  line-height: 20px;
`;

const SearchStyled = styled(Search)`
  color: ${Colors.SECONDARY_DARK};
`;

const StyledTable = styled(Table)`
  & ${ThContainer} {
    height: 56px;
    display: flex;
    align-items: center;
    background-color: transparent;
    ${({ haspadding }) => haspadding
      && `
      padding-right: 32px;
    `};
  }

  & ${TdContainer} {
    height: 56px;
    display: flex;
    align-items: center;
    ${({ haspadding }) => haspadding
      && `
      padding-right: 32px;
    `};
  }
`;

const StyledStatusContainer = styled.div`
  background-color: ${({ status }) => backgroundColorByStatus[status] || Colors.GRAY_LIGHT};
  border: 1px solid
    ${({ status }) => colorsByStatus[status] || Colors.GRAY_DARK};
  border-radius: 16px;
  padding: 4px 8px;
  margin-left: -16px;
`;

const StyledStatusText = styled(Text)`
  font-size: 14px;
  line-height: 20px;
  font-weight: 700;
  color: ${({ status }) => colorsByStatus[status] || Colors.GRAY_DARK};
`;

export const colorsByStatus = {
  [OrderStatusEnum.ORDER_PENDING_APPROVAL]: Colors.WARNING_SHADES_MAIN,
  [OrderStatusEnum.ORDER_STATUS_APROVED]: Colors.SUCCESS_LIGHT,
  [OrderStatusEnum.ORDER_NOT_APPROVED]: Colors.DANGER,
  [OrderStatusEnum.ORDER_CANCELED]: Colors.DANGER,
  [OrderStatusEnum.BILLING_ORDER]: Colors.SECONDARY_DARK,
  [OrderStatusEnum.PREPARING_ORDER]: Colors.INDIGO_LIGHT,
  [OrderStatusEnum.ORDER_SENT_TO_TRANSPORT]: Colors.AMBER_LIGHT,
  [OrderStatusEnum.ORDER_IN_TRANSPORT]: Colors.GRAY_MEDIUM,
  [OrderStatusEnum.DELIVERY_ATTEMPT]: Colors.PINK_MEDIUM,
  [OrderStatusEnum.ORDER_DELIVERED]: Colors.GREEN_DARKER,
  [OrderStatusEnum.ORDER_NOT_DELIVERED]: Colors.TEAL_DARK,
  [OrderStatusEnum.ORDER_REFUSED]: Colors.BLUE_DARK,
  [OrderStatusEnum.ORDER_STATUS_ERP_EXPORT]: Colors.YELLOW_DARKER,
  [OrderStatusEnum.PREPARED_ORDER]: Colors.INDIGO_DARKER,
};

export const backgroundColorByStatus = {
  [OrderStatusEnum.ORDER_PENDING_APPROVAL]: Colors.WARNING_LIGHT,
  [OrderStatusEnum.ORDER_STATUS_APROVED]: Colors.SUCCESS_LIGHT_SHADE,
  [OrderStatusEnum.ORDER_NOT_APPROVED]: Colors.RED_LIGHT,
  [OrderStatusEnum.ORDER_CANCELED]: Colors.RED_LIGHT,
  [OrderStatusEnum.BILLING_ORDER]: Colors.SECONDARY_SHADE,
  [OrderStatusEnum.PREPARING_ORDER]: Colors.INDIGO_LIGHT_SHADE,
  [OrderStatusEnum.ORDER_SENT_TO_TRANSPORT]: Colors.AMBER_LIGHT_SHADE,
  [OrderStatusEnum.ORDER_IN_TRANSPORT]: Colors.GRAY_LIGHEST_LIGHT,
  [OrderStatusEnum.DELIVERY_ATTEMPT]: Colors.PINK_LIGHT_SHADE,
  [OrderStatusEnum.ORDER_DELIVERED]: Colors.GREEN_DARKER_SHADE,
  [OrderStatusEnum.ORDER_NOT_DELIVERED]: Colors.TEAL_LIGHT_SHADE,
  [OrderStatusEnum.ORDER_REFUSED]: Colors.BLUE_LIGHT_SHADE,
  [OrderStatusEnum.ORDER_STATUS_ERP_EXPORT]: Colors.YELLOW_LIGHT_SHADE,
  [OrderStatusEnum.PREPARED_ORDER]: Colors.GRAY_LIGHEST_LIGHT,
};

const OrdersTable = ({
  userData,
  data,
  loading,
  currentPage,
  totalItems,
  itemsPerPage,
  onPageChange,
  onChangeItemsPerPage,
  onExportSelectedData,
  onApproveSelectedData,
  onCancelSelectedData,
  onChangeDataOrder,
  dataOrder,
  onRequestOrderDetail,
  onClearFilter,
  pairingActive,
  multiselect,
  radiobutton,
  slideDataActive,
  handleRowCheck,
  checkOrderNumber,
  onPairOrder,
  ...props
}) => {
  const history = useHistory();
  const currentWindowSize = useWindowSize();

  const _renderTypeTextCell = (translatedValue, originalValue) => (
    <Tooltip title={translatedValue || ''} arrow placement="top">
      <StyledTypeContainer type={originalValue}>
        <StyledTypeText type={originalValue}>
          {translatedValue || '--'}
        </StyledTypeText>
      </StyledTypeContainer>
    </Tooltip>
  );

  const _renderBasicTextCell = (value) => (
    <Tooltip title={value || ''} arrow placement="top">
      <div>
        <Text
          numberOfLines={2}
          style={{ fontSize: 14, maxWidth: 100, wordWrap: 'break-word' }}
          key={`key-${value}`}
        >
          {value || '--'}
        </Text>
      </div>
    </Tooltip>
  );

  const _renderCicleTextCell = (value) => (
    <Tooltip title={value} arrow placement="top">
      <div>
        <Text
          numberOfLines={2}
          style={{ fontSize: 14, maxWidth: 100, wordWrap: 'break-word' }}
        >
          {value || 'Pedido avulso'}
        </Text>
      </div>
    </Tooltip>
  );

  const _renderStatusTextCell = (status) => (
    <Tooltip
      title={OrderStatusText[status] || 'Sem status'}
      arrow
      placement="top"
    >
      <StyledStatusContainer status={status}>
        <StyledStatusText
          status={status}
          numberOfLines={1}
          style={{ fontSize: 14 }}
        >
          {OrderStatusText[status] || 'Sem status'}
        </StyledStatusText>
      </StyledStatusContainer>
    </Tooltip>
  );

  const _renderDoubleRow = (firstValue, secondValue) => (
    <DoubleRowWrapper>
      <DobleRowPrimaryValue>{firstValue || '--'}</DobleRowPrimaryValue>
      <DobleRowSecondaryValue>{secondValue || '--'}</DobleRowSecondaryValue>
    </DoubleRowWrapper>
  );

  const _renderStatusERPTextCell = (status) => (
    <ContainerText>
      {OrderStatusERPIcon[status]?.() || ''}
      <Text>{OrderStatusERPText[status] || ''}</Text>
    </ContainerText>
  );

  const columnConfig = {
    [columnType.REF_NUMBER]: {
      order: 1,
      width: 140,
    },
    [columnType.NFE_NUMBER]: {
      order: 2,
      noSorting: true,
      width: 128,
    },
    [columnType.ORDER_NUMBER]: {
      order: 3,
      noSorting: true,
      width: 98,
    },
    [columnType.AGENT]: {
      order: 4,
      noSorting: true,
      width: 110,
    },
    [columnType.APPROVED_BY]: {
      order: 5,
      noSorting: true,
      width: 78,
    },
    [columnType.TYPE]: {
      order: 6,
      width: 140,
      noSorting: true,
    },
    [columnType.PRE_ORDER_QUANTITY]: {
      order: 7,
      noSorting: true,
      width: 130,
    },
    [columnType.STATUS]: {
      order: 8,
      width: 120,
    },
    [columnType.PROMOTIONAL_CICLE]: {
      order: 9,
      noSorting: true,
      width: 103,
    },
    [columnType.DELIVERY]: {
      order: 10,
      width: 93,
    },
    [columnType.STATUS_ERP]: {
      order: 11,
      width: 118,
    },
    [columnType.ACTION]: {
      order: 12,
      noHeader: true,
      noSorting: true,
      width: 10,
    },
  };

  const columnAgentsConfig = {
    [columnType.REF_NUMBER]: {
      order: 1,
      width: 140,
    },
    [columnType.NFE_NUMBER]: {
      order: 2,
      noSorting: true,
      width: 145,
    },
    [columnType.ORDER_NUMBER]: {
      order: 3,
      noSorting: true,
      width: 120,
    },
    [columnType.AGENT]: {
      order: 4,
      noSorting: true,
      width: 130,
    },
    [columnType.APPROVED_BY]: {
      order: 5,
      noSorting: true,
      // width: 120,
    },
    [columnType.TYPE]: {
      order: 6,
      width: 140,
      noSorting: true,
    },
    [columnType.PRE_ORDER_QUANTITY]: {
      order: 7,
      noSorting: true,
      width: 130,
    },
    [columnType.STATUS]: {
      order: 8,
      width: 100,
    },
    [columnType.PROMOTIONAL_CICLE]: {
      order: 9,
      noSorting: true,
      width: 100,
    },
    [columnType.DELIVERY]: {
      order: 10,
      width: 80,
    },
    [columnType.STATUS_ERP]: {
      order: 11,
      width: 100,
    },
    [columnType.ACTION]: {
      order: 12,
      noHeader: true,
      noSorting: true,
      width: 10,
    },
  };

  const components = {
    [columnType.REF_NUMBER]: (value, list, index) => {
      const createdAt = list[index]?.createdAt;
      const dateFormatted = createdAt
        ? new Date(createdAt).toLocaleDateString('pt-BR')
        : undefined;

      return _renderDoubleRow(value, dateFormatted);
    },
    [columnType.NFE_NUMBER]: (value, list, index) => {
      const nfeSerie = list[index]?.nfeSerie;
      return _renderDoubleRow(value, nfeSerie);
    },
    [columnType.ORDER_NUMBER]: _renderBasicTextCell,
    [columnType.AGENT]: (agent) => _renderBasicTextCell(agent?.name),
    [columnType.APPROVED_BY]: (approvedBy) => _renderBasicTextCell(approvedBy?.name),
    [columnType.TYPE]: (value) => _renderTypeTextCell(translateColumnType(value), value),
    [columnType.PRE_ORDER_QUANTITY]: (value) => _renderBasicTextCell(value),
    [columnType.STATUS]: _renderStatusTextCell,
    [columnType.PROMOTIONAL_CICLE]: _renderCicleTextCell,
    [columnType.DELIVERY]: (value) => _renderBasicTextCell(value && format(new Date(value), 'dd/MM/yyyy')),
    [columnType.STATUS_ERP]: _renderStatusERPTextCell,
    [columnType.ACTION]: () => (
      <ActionsContainer>
        <SearchStyled />
      </ActionsContainer>
    ),
  };

  const componentsAgents = {
    [columnType.REF_NUMBER]: (value, list, index) => {
      const createdAt = list[index]?.createdAt;
      const dateFormatted = createdAt
        ? new Date(createdAt).toLocaleDateString('pt-BR')
        : undefined;

      return _renderDoubleRow(value, dateFormatted);
    },
    [columnType.NFE_NUMBER]: (value, list, index) => {
      const nfeSerie = list[index]?.nfeSerie;
      return _renderDoubleRow(value, nfeSerie);
    },
    [columnType.ORDER_NUMBER]: _renderBasicTextCell,
    [columnType.AGENT]: (agent) => _renderBasicTextCell(agent?.name),
    [columnType.APPROVED_BY]: (approvedBy) => _renderBasicTextCell(approvedBy?.name),
    [columnType.TYPE]: (value) => _renderTypeTextCell(translateColumnType(value), value),
    [columnType.PRE_ORDER_QUANTITY]: (value) => _renderBasicTextCell(value),
    [columnType.STATUS]: _renderStatusTextCell,
    [columnType.PROMOTIONAL_CICLE]: _renderCicleTextCell,
    [columnType.DELIVERY]: (value) => _renderBasicTextCell(value && format(new Date(value), 'dd/MM/yyyy')),
    [columnType.STATUS_ERP]: _renderStatusERPTextCell,
    [columnType.ACTION]: () => (
      <ActionsContainer>
        <SearchStyled />
      </ActionsContainer>
    ),
  };

  const handleOnRequestOrderDetail = async (data) => {
    onRequestOrderDetail(data);
  };

  const translateColumnType = (category) => {
    switch (category) {
    case OrderCategoryEnum.PROMOTIONAL:
      return 'PROMO';
    case OrderCategoryEnum.AMOSTRA_GRATIS:
      return 'AMG';
    case OrderCategoryEnum.AMOSTRA_GRATIS_TRIBUTADA:
      return 'AGT';
    default:
      return '--';
    }
  };

  return (
    <>
      <StyledTable
        name="orders-table"
        isLoading={loading}
        footerText="Pedidos por página: "
        multiselect={
          userData.userRole === USER_ROLES.AGENT ? null : multiselect
        }
        radiobutton={
          userData.userRole === USER_ROLES.AGENT ? null : radiobutton
        }
        data={data}
        handleHeaderTitle={(key) => columnLabel[key]}
        columnConfig={
          userData.userRole === USER_ROLES.AGENT
            ? columnAgentsConfig
            : columnConfig
        }
        components={
          userData.userRole === USER_ROLES.AGENT ? componentsAgents : components
        }
        paginationProps={{
          currentPage,
          totalItems,
          itemsPerPage,
          onPageChange,
        }}
        onChangeItemsPerPage={onChangeItemsPerPage}
        dataOrder={dataOrder}
        onOrderColumn={onChangeDataOrder}
        rowWrapper={() => ({ RowWrapper, rowWrapperProps: {} })}
        onRowClick={(data) => {
          // handleOnRequestOrderDetail(data);
          history.push(
            `${RoutePaths.PRE_ORDER_DETAIL.replace(':id', data.id)}`,
          );
        }}
        onRowCheck={(index) => {
          handleRowCheck(data[index].id);
        }}
        // haspadding={userData.userRole === USER_ROLES.AGENT ? 1 : 0}
        groupFirstColumns={
          currentWindowSize.width > BreakpointValues.sm
            ? null
            : userData.userRole === USER_ROLES.AGENT
              ? 1
              : 2
        }
        renderEmptyState={() => (
          <StyledEmptyStateWithAction
            alignCenter
            emptyStateText="Nenhum pedido encontrado :("
            emptyStateDescription="Realize uma nova busca ou atualize a página"
            renderAction={() => (
              <StyledEmptyActionsContainer>
                <Button
                  variant="outlined"
                  onClick={() => {
                    window.location.reload();
                  }}
                >
                  Atualizar
                </Button>
                <ButtonDivider />
                {/* <Button variant="contained" onClick={onClearFilter}>
                  Limpar Filtros
                </Button> */}
              </StyledEmptyActionsContainer>
            )}
          />
        )}
        renderOutside={(selectedRows, setSelected) => pairingActive ? (
          <FloatBatchActionsMenu
            show={!!selectedRows.length}
            selectedLabel={
              slideDataActive.orderNumber
                ? `WMS ${slideDataActive.orderNumber} e ${checkOrderNumber}`
                : 'Nenhum pedido WMS para parear'
            }
            selectedItems={selectedRows}
            pairingActive
            onClose={() => {
              setSelected([]);
            }}
          >
            {slideDataActive.orderNumber && (
              <StyledTouchableText
                onClick={() => {
                  onPairOrder({
                    orderNumber: slideDataActive.orderNumber,
                    orderId: checkOrderNumber,
                  });
                  setSelected([]);
                }}
              >
                Parear pedido
              </StyledTouchableText>
            )}
          </FloatBatchActionsMenu>
        ) : (
          userData.userRole === USER_ROLES.COMPANY && (() => {
            const hasAnyCanceled = selectedRows.some(
              (row) => row.data?.status === OrderStatusEnum.ORDER_CANCELED,
            );
            return (
              <FloatBatchActionsMenu
                width="685px"
                show={!!selectedRows.length}
                selectedLabel={
                  selectedRows.length > 1
                    ? 'pedidos selecionados'
                    : 'pedido selecionado'
                }
                selectedItems={selectedRows}
                onClose={() => {
                  setSelected([]);
                }}
              >
                {!hasAnyCanceled && (
                  <StyledTouchableText
                    onClick={() => {
                      onApproveSelectedData(selectedRows);
                      setSelected([]);
                    }}
                  >
                    {selectedRows.length > 1 ? 'Aprovar pedidos' : 'Aprovar pedido'}
                  </StyledTouchableText>
                )}
                {!hasAnyCanceled && (
                  <StyledTouchableText
                    onClick={() => {
                      onCancelSelectedData(selectedRows);
                      setSelected([]);
                    }}
                  >
                    {selectedRows.length > 1 ? 'Cancelar pedidos' : 'Cancelar pedido'}
                  </StyledTouchableText>
                )}
                <StyledTouchableText
                  onClick={() => {
                    onExportSelectedData(selectedRows);
                    setSelected([]);
                  }}
                >
                  {selectedRows.length > 1 ? 'Exportar pedidos' : 'Exportar pedido'}
                </StyledTouchableText>
              </FloatBatchActionsMenu>
            );
          })()
        )}
        {...props}
      />
    </>
  );
};

OrdersTable.propTypes = {
  currentPage: PropTypes.number.isRequired,
  data: PropTypes.array.isRequired,
  itemsPerPage: PropTypes.number.isRequired,
  totalItems: PropTypes.number.isRequired,
  loading: PropTypes.bool,
  onChangeItemsPerPage: PropTypes.func.isRequired,
  onPageChange: PropTypes.func.isRequired,
  onApproveSelectedData: PropTypes.func.isRequired,
  onCancelSelectedData: PropTypes.func.isRequired,
  onExportSelectedData: PropTypes.func.isRequired,
  onChangeDataOrder: PropTypes.func,
  onRequestOrderDetail: PropTypes.func.isRequired,
  pairingActive: PropTypes.bool,
  multiselect: PropTypes.bool,
  radiobutton: PropTypes.bool,
  slideDataActive: PropTypes.object,
  handleRowCheck: PropTypes.func,
  checkOrderNumber: PropTypes.number,
};

OrdersTable.defaultProps = {
  loading: false,
  onChangeDataOrder: undefined,
  pairingActive: false,
  multiselect: false,
  radiobutton: false,
  slideDataActive: {},
  handleRowCheck: () => {},
  checkOrderNumber: null,
};

export default OrdersTable;
